import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./tabGroupV2.html";
import { Routes } from '~/modules/routes';
import { navigate } from '~/modules/stores/routerStore';
import { upsertQueryParams } from '~/modules/utilities/router';

/**
 * v2 tab group — embedded by `pages/listingDetailV2` in place of v1's
 * `wds/tabGroup`.
 *
 * This is a STANDALONE WHOLESALE COPY of `wds/tabGroup`, not a subclass. It was
 * forked because the v1 `pages/listingDetail` page rendered `wds-tab-group`
 * (that page has since been deleted, W-24557579); the v2 listing page needs a
 * tighter vertical rhythm (32px above/below the tab list instead of 48px) to
 * match the modernized layout (Figma: Try&Buy Listing Page Modernization). We
 * deliberately did NOT edit `wds/tabGroup` in place or make v2 inherit from it:
 * either would have leaked the v2 spacing into other `wds-tab-group` consumers.
 * Copying keeps `wds/tabGroup` untouched and keeps the v2 spacing self-contained
 * in this folder — no super() to unwind, no shared consumer to repoint.
 *
 * Divergences from v1 are intentional and localized: `tabGroupV2.css` tightens
 * the tab-list top/bottom margin, and an `@api flush` mode drops the list's own
 * underline inside the collapsed nav bar. The behavior below is identical to v1.
 *
 * Tradeoff we accept: `git blame` on this file threads back only to the fork
 * commit; use `git blame -C -C` / `git log --follow` to reach the pre-fork
 * history, which also stays reachable via `git log -- <v1 path>` after v1 is
 * deleted.
 */

class TabGroupV2 extends LightningElement {
  constructor(...args) {
    super(...args);
    this.tabs = void 0;
    /**
     * When true, the tab list drops its own bottom underline. Used when the tab
     * group is embedded in the collapsed nav bar, where the bar's own
     * `border-bottom` provides the single full-width divider instead.
     */
    this.flush = false;
    /**
     * Optional prefix applied to each rendered tab anchor's `id`. The listing
     * page co-mounts two instances of this group from the same `tabs` array (the
     * inline `.tab-bar` and the collapsed nav bar). Anchor ids are NOT scoped by
     * the browser build, so without a namespace both instances would emit the
     * same literal id (`overview-tab` ×2) — invalid HTML that makes a bare
     * `#overview-tab` selector ambiguous (strict-mode locator violations in e2e,
     * and ambiguous `aria-labelledby`/`aria-controls` idref resolution). The
     * collapsed instance passes `id-namespace="collapsed-"` so only the main bar
     * keeps the canonical `overview-tab`. Analytics `name` stays `tab.id`
     * (unprefixed) for both, and the parent matches panels↔tabs by id *suffix*
     * (`id.includes('overview-tab')`), which a prefix leaves intact.
     */
    this.idNamespace = '';
    /**
     * When true, the mobile dropdown behaves as an inline collapsed-header
     * control (Figma 7909:93145): the trigger stays a bare content-width
     * label+chevron sharing the header row (no hairline box), and its option
     * panel drops as a full-bleed overlay directly beneath the header rather
     * than being confined to the trigger's own (narrow) column. Used by the
     * collapsed nav bar on tablet/phone; the main tab bar leaves it false, so
     * its dropdown keeps the boxed trigger and an in-flow panel.
     */
    this.dropdownFullBleed = false;
    /**
     * True when parent passes `mobile` (stacked vertical tab layout): the tab
     * list collapses behind a dropdown trigger, and ArrowUp/Down change tab
     * while ArrowLeft/Right do not. `@track` so the template's trigger/panel
     * react when the parent flips the `mobile` prop on viewport change.
     */
    this.stackedTabLayout = false;
    /** Mobile only: whether the collapsed tab dropdown is currently expanded. */
    this.isDropdownOpen = false;
    /**
     * Bound document listener for outside-press close; added only while open.
     * Listens on `mousedown` (not `click`) so the trigger's own opening click
     * can't immediately re-close the panel: the opening interaction's mousedown
     * fires *before* `openDropdown` registers this listener, so only a
     * subsequent press elsewhere closes it (same pattern as `ace/orgContextMenu`).
     *
     * "Inside" is tested against the actual trigger/panel DOM nodes, not the
     * component instance — `event.composedPath().includes(this)` never matches
     * because `this` is the LWC element instance, not a node in the event path.
     * With that broken check, a press *on an option* was treated as outside and
     * closed the panel on `mousedown`, tearing the `<ul>` out of layout before
     * the option's `click` could fire — so selecting a tab never navigated. We
     * mirror `ace/orgContextMenu`: match either the composed path (shadow-safe)
     * or `contains(target)`.
     */
    this.handleDocumentMouseDown = event => {
      if (!this.isDropdownOpen) {
        return;
      }
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      const target = event.target;
      const trigger = this.template.querySelector('.tab-menu-trigger');
      const panel = this.template.querySelector('ul[role="tablist"]');
      const insideTrigger = !!trigger && (path.includes(trigger) || !!target && trigger.contains(target));
      const insidePanel = !!panel && (path.includes(panel) || !!target && panel.contains(target));
      if (!insideTrigger && !insidePanel) {
        this.closeDropdown();
      }
    };
    /**
     * Tab label type-style. v2 sizes tabs at `display-8` so the tab text matches
     * the listing name shown in the collapsed nav bar's compact header (which is
     * also `display-8`). Same on mobile and desktop — the stacked-vs-inline
     * layout difference is driven by `stackedTabLayout`, not the font size.
     */
    this.responsiveState = {
      tabTypeStyle: 'display-8'
    };
    this.lastRegisteredTabIdsSignature = null;
  }
  /**
   * `tabs` with an `anchorId` (`idNamespace` + `id`) added per row. The
   * template renders the anchor `id`/`tab-key` from `anchorId` while keeping
   * `name`/label/url on the original `tab`, so the analytics event name and the
   * parent's suffix match are unchanged.
   */
  get renderTabs() {
    return (this.tabs ?? []).map(tab => ({
      ...tab,
      anchorId: `${this.idNamespace}${tab.id}`
    }));
  }
  get listClass() {
    const classes = [];
    if (this.flush) {
      classes.push('flush');
    }
    // Stacked (mobile) mode turns the tab list into a disclosure panel: the
    // `<ul>` is collapsed by default and only revealed when the trigger is
    // toggled open (Figma Tab Navigation/Mobile 7888:72963). Scoped to the
    // non-`flush` (main) tab bar — the `flush` collapsed-nav bar keeps its
    // own compact horizontal treatment and is unaffected.
    if (this.showMobileDropdown) {
      classes.push('tab-menu');
      if (this.dropdownFullBleed) {
        classes.push('tab-menu--floating');
      }
      if (this.isDropdownOpen) {
        classes.push('tab-menu--open');
      }
    }
    return classes.join(' ');
  }

  /**
   * The mobile dropdown replaces the inline tab list only for the main
   * (non-`flush`) tab bar. The `flush` variant lives inside the collapsed nav
   * bar, which has its own compact layout, so it never collapses to a dropdown.
   */
  get showMobileDropdown() {
    return this.stackedTabLayout && !this.flush;
  }

  /**
   * Trigger button class. The trigger stays visible while the drawer is open
   * in BOTH placements (the boxed main tab bar and the full-bleed collapsed
   * header). Both rotate the chevron 180°. The trigger doubles as the tap-to-collapse
   * control. Keeping it anchored also
   * preserves the `aria-controls` registration and the Escape-key focus target
   * across the open/close cycle. (The active tab label does then appear both on
   * the trigger and as the highlighted first list row — an accepted trade-off
   * for always offering a visible collapse affordance.)
   */
  get triggerClass() {
    const classes = ['tab-menu-trigger'];
    // Collapsed-header (full-bleed) variant: a bare inline label+chevron
    // that shares the header row, with no hairline box (Figma 7909:93145).
    if (this.dropdownFullBleed) {
      classes.push('tab-menu-trigger--inline');
    }
    return classes.join(' ');
  }
  /** Label of the active tab, shown on the collapsed mobile dropdown trigger. */
  get activeTabLabel() {
    return this.tabs?.find(tab => tab.active)?.label ?? '';
  }

  /** Always `chevrondown`; open state rotates it 180° in CSS (boxed and collapsed). */
  get dropdownChevronIcon() {
    return 'chevrondown';
  }

  /** `aria-expanded` wants a string; mirror the open state onto the trigger. */
  get dropdownExpanded() {
    return String(this.isDropdownOpen);
  }
  toggleDropdown() {
    if (this.isDropdownOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }
  openDropdown() {
    this.isDropdownOpen = true;
    // Lets listing detail unclip the collapsed bar only while this panel is
    // open, so the bar's 0fr→1fr reveal can keep overflow hidden.
    this.setAttribute('data-dropdown-open', '');
    // The trigger stays visible while open in both placements (`triggerClass`),
    // so focus can stay on it — no need to move focus into the option list.
    document.addEventListener('mousedown', this.handleDocumentMouseDown);
  }
  closeDropdown() {
    if (!this.isDropdownOpen) {
      return;
    }
    this.isDropdownOpen = false;
    this.removeAttribute('data-dropdown-open');
    this.setAttribute('data-dropdown-closing', '');
    document.removeEventListener('mousedown', this.handleDocumentMouseDown);
    if (this.dropdownCloseTimer !== undefined) {
      clearTimeout(this.dropdownCloseTimer);
    }
    this.dropdownCloseTimer = setTimeout(() => {
      this.removeAttribute('data-dropdown-closing');
      this.dropdownCloseTimer = undefined;
    }, TabGroupV2.DROPDOWN_CLOSE_MS);
  }

  /**
   * Guarantee client-side (SPA) navigation on a tab click, and collapse the
   * mobile dropdown once a tab is chosen.
   *
   * Each tab anchor is slotted into a `wds-link`, which normally intercepts the
   * click and routes client-side. But `wds-link` binds the anchor's `onclick`
   * only ONCE — its `renderedCallback` is guarded by a cached `_destination`, so
   * after the first render it never rebinds. Our tab list swaps every anchor
   * between the `if:true={tab.active}` / `if:false` branches on each tab change,
   * so LWC destroys the old `<a>` and creates a fresh one that `wds-link` never
   * re-wires — its native `href` then triggers a full page reload (the very
   * regression the reviews-count link, which uses the global `i18n-link`
   * delegation, doesn't suffer).
   *
   * This `<ul>`-level handler is bound by the template to the stable list
   * element, so it always fires regardless of anchor churn. If `wds-link` did
   * handle the click (its binding was still in effect), it already called
   * `preventDefault`, so we defer via the `defaultPrevented` guard and avoid a
   * double navigation. Otherwise we take over and route client-side ourselves.
   *
   * The same slotted-anchor replacement that strands `wds-link`'s one-time
   * `onclick` binding also strands `wds-analytics-instrument`'s click listener
   * (its `hasLoaded` guard blocks rebinding onto the fresh anchor), so the
   * tab-click `analytics` event that normally powers tab engagement attribution
   * is lost too. When we take over, re-emit that event ourselves — matching the
   * contract `wds-link` (CTA url/label/name) plus this group's own
   * `handleAnalytics` (`elementType: 'tab'`) would have produced — guarded by
   * the click's `analyticsHandled` flag so we never double-count on the first
   * render, when the instrument's listener is still bound and fires it.
   */
  handleTabListClick(event) {
    const anchor = event.target?.closest?.('a[tab-key]');
    if (anchor) {
      const tabKey = anchor.getAttribute('tab-key');
      const tab = this.tabs?.find(candidate => `${this.idNamespace}${candidate.id}` === tabKey);
      if (tab) {
        if (!event.defaultPrevented) {
          event.preventDefault();
          const clickEvent = event;
          if (!clickEvent.analyticsHandled) {
            clickEvent.analyticsHandled = true;
            this.dispatchEvent(new CustomEvent('analytics', {
              bubbles: true,
              composed: true,
              detail: {
                elementType: 'tab',
                eventType: 'click',
                link: {
                  callToAction: {
                    url: tab.url,
                    label: tab.label,
                    name: tab.id
                  }
                }
              }
            }));
          }
          this.closeMobileDropdownAndRefocus();
          this.changeTab(tab);
          return;
        }
        this.closeMobileDropdownAndRefocus();
        this.dispatchTabChange(tab);
        return;
      }
    }
    this.closeMobileDropdownAndRefocus();
  }

  /**
   * Collapse the mobile dropdown after a selection, moving focus to the
   * persistent trigger BEFORE clearing the open state.
   *
   * The chosen tab anchor lives inside the drawer panel, which collapses to
   * `max-height: 0` / `visibility: hidden` on close. In Chromium, removing
   * `tab-menu--open` while that anchor still holds focus immediately drops
   * focus to the document root — even though the panel stays painted for the
   * exit animation — dumping keyboard users at the top of the page on every
   * mobile/tablet tab switch. The parent's post-navigation `focusActiveTab()`
   * would otherwise re-target the same closing anchor. Focusing the
   * always-present trigger first keeps focus on a visible control. No-op unless
   * the mobile dropdown is actually open (desktop / flush bar are unaffected).
   */
  closeMobileDropdownAndRefocus() {
    if (!this.showMobileDropdown || !this.isDropdownOpen) {
      return;
    }
    this.template.querySelector('.tab-menu-trigger')?.focus();
    this.closeDropdown();
  }
  disconnectedCallback() {
    if (this.dropdownCloseTimer !== undefined) {
      clearTimeout(this.dropdownCloseTimer);
      this.dropdownCloseTimer = undefined;
    }
    this.removeAttribute('data-dropdown-open');
    this.removeAttribute('data-dropdown-closing');
    // Guard against a leaked listener if the element is torn down while open.
    document.removeEventListener('mousedown', this.handleDocumentMouseDown);
  }
  get mobile() {
    //LWC needs a getter whenever using a setter, even though the 'mobile' property is not being directly used inside the template
    return this.responsiveState;
  }
  set mobile(isMobile) {
    this.stackedTabLayout = Boolean(isMobile);
    // Leaving mobile (e.g. rotate to landscape / resize to tablet) reverts to
    // the inline tab list; make sure a lingering-open dropdown is closed and
    // its document listener removed.
    if (!this.stackedTabLayout) {
      this.closeDropdown();
    }
  }
  changeTab(newTab) {
    this.dispatchTabChange(newTab);
    const hashIndex = newTab.url.indexOf('#');
    const hash = hashIndex >= 0 ? newTab.url.slice(hashIndex) : '';
    navigate(`${Routes.LISTING}?${upsertQueryParams(['tab', newTab.code])}${hash}`);
  }
  dispatchTabChange(tab) {
    this.dispatchEvent(new CustomEvent('tabchange', {
      bubbles: true,
      composed: true,
      detail: {
        code: tab.code
      }
    }));
  }

  /**
   * Escape handler for the dropdown trigger button. The tab-list keydown
   * listener (`handleTabKeydown`) only fires for focus inside the `<ul>`, and
   * the trigger is its sibling — so keydowns on the trigger never reach it. The
   * trigger stays visible and holds focus while the drawer is open (both
   * placements), so without this handler keyboard / screen-reader users would
   * have no way to dismiss the open menu via Escape. Close and keep focus on the
   * trigger (where it already is). Only Escape is handled here so the trigger's
   * other keys (Enter/Space to toggle via the native button, Tab to leave) stay
   * native.
   */
  // @ts-expect-error * Automated Type Error Fix
  handleTriggerKeydown(event) {
    if (event.key === 'Escape' && this.isDropdownOpen) {
      this.closeDropdown();
      event.preventDefault();
    }
  }

  // @ts-expect-error * Automated Type Error Fix
  handleTabKeydown(event) {
    // Only the keys we handle call preventDefault(). A blanket default that
    // prevented every other key would swallow the browser's native Enter/Space
    // activation of the focused tab anchor — leaving keyboard users unable to
    // open a tab except via the arrow keys. Unhandled keys (Enter, Space,
    // Tab, …) fall through untouched so the anchor / focus order behave
    // natively.
    switch (event.key) {
      case 'ArrowLeft':
        if (!this.stackedTabLayout) {
          this.selectPreviousTab();
          event.preventDefault();
        }
        break;
      case 'ArrowRight':
        if (!this.stackedTabLayout) {
          this.selectNextTab();
          event.preventDefault();
        }
        break;
      case 'ArrowUp':
        if (this.stackedTabLayout) {
          this.selectPreviousTab();
          event.preventDefault();
        }
        break;
      case 'ArrowDown':
        if (this.stackedTabLayout) {
          this.selectNextTab();
          event.preventDefault();
        }
        break;
      case 'Home':
        this.selectFirstTab();
        event.preventDefault();
        break;
      case 'End':
        this.selectLastTab();
        event.preventDefault();
        break;
      case 'Escape':
        // Collapse the mobile dropdown and return focus to its trigger.
        if (this.showMobileDropdown && this.isDropdownOpen) {
          this.closeDropdown();
          this.template.querySelector('.tab-menu-trigger')?.focus();
          event.preventDefault();
        }
        break;
      default:
        // Leave every other key (incl. Enter/Space native activation) alone.
        break;
    }
  }
  selectPreviousTab() {
    const activeTab = this.tabs.filter(tab => tab.active)[0];
    const previousTabOrder = activeTab.order - 1 < 1 ? this.tabs.length : activeTab.order - 1;
    const previousTab = this.tabs.filter(tab => tab.order === previousTabOrder)[0];
    this.changeTab(previousTab);
  }
  selectNextTab() {
    const activeTab = this.tabs.filter(tab => tab.active)[0];
    const nextTabOrder = activeTab.order + 1 > this.tabs.length ? 1 : activeTab.order + 1;
    const nextTab = this.tabs.filter(tab => tab.order === nextTabOrder)[0];
    this.changeTab(nextTab);
  }
  selectFirstTab() {
    const firstTab = this.tabs.find(tab => tab.order === 1);
    if (firstTab) {
      this.changeTab(firstTab);
    }
  }
  selectLastTab() {
    const lastTab = this.tabs.find(tab => tab.order === this.tabs.length);
    if (lastTab) {
      this.changeTab(lastTab);
    }
  }

  /**
   * Reveal the stacked trigger (or the host on desktop) by scrolling it down to
   * rest just below the sitewide nav — but only when it has scrolled *above* that
   * resting line. Listing detail uses this on tab pick so an open in-flow list
   * does not become the scroll target.
   */
  scrollTriggerIntoView(options) {
    const trigger = this.template?.querySelector('.tab-menu-trigger');
    // Fall back to the host element, not `this`: the LWC component instance
    // is not a DOM Element, so `getComputedStyle(this)` throws.
    const target = trigger ?? this.template?.host;
    if (!target) {
      return;
    }
    // Native scrollIntoView on a 100vw bar can shift horizontally, and with
    // an in-flow open list it treats the tall box as the target. Scroll the
    // window so the trigger's top sits at its scroll-margin (site header).
    const margin = Number.parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
    const delta = target.getBoundingClientRect().top - margin;
    // Upward reveal only. A negative delta means the trigger has scrolled above
    // its resting line (behind/above the nav, or picked from the collapsed
    // drawer) — pull it back down into view. A non-negative delta means it is
    // already at or below that line and fully visible, so do nothing: a tab
    // pick must not scroll a visible page *down* (which would hide the summary
    // hero and land inconsistently across tabs of different heights), and
    // re-picking the active tab must not move the page at all.
    if (delta > -1) {
      return;
    }
    const behavior = options?.behavior === 'smooth' ? 'smooth' : 'auto';
    if (typeof window.scrollBy === 'function') {
      window.scrollBy({
        top: delta,
        left: 0,
        behavior
      });
      return;
    }
    target.scrollIntoView(options);
  }

  /* This is going to be called from the parent component to set the right aria-controls attribute */
  // @ts-expect-error * Automated Type Error Fix
  setAriaControls(tabId, panelId) {
    this.template.querySelector(`a[id="${tabId}"]`)?.setAttribute('aria-controls', panelId);
  }

  /* This is going to be called from the parent component whenever a tab change is fired */
  focusActiveTab(options) {
    // When the mobile dropdown is collapsed, the active tab anchor lives
    // inside the collapsed drawer panel, so focusing it would
    // silently drop focus to the document root. Route focus to the persistent
    // trigger instead. Desktop — and the flush collapsed-nav bar, which never
    // becomes a dropdown (`showMobileDropdown` is false while `flush`) — keep
    // focusing the visible active anchor.
    const target = this.showMobileDropdown && !this.isDropdownOpen ? this.template.querySelector('.tab-menu-trigger') : this.template.querySelector('li[active] > a');
    // Forward `options` so a caller that has already positioned the page can
    // refocus with `{ preventScroll: true }`. The active `<a>` carries no
    // scroll-margin (only the host and the stacked trigger do), so a default
    // focus would scroll-into-view align the anchor to the viewport top and
    // yank the page there — see the listing's collapsed-nav pick.
    target?.focus(options);
  }
  renderedCallback() {
    /* Re-register whenever conditional tabs change so the parent can wire every current anchor
     * bidirectionally to its panel. The signature guard avoids redispatching for active-state
     * renders where the set of tab IDs is unchanged. */
    const tabsIds = Array.from(this.template.querySelectorAll('a[role="tab"]')).map(anchor => anchor.getAttribute('id')).filter(id => Boolean(id));
    const signature = JSON.stringify(tabsIds);
    if (signature === this.lastRegisteredTabIdsSignature) {
      return;
    }
    this.lastRegisteredTabIdsSignature = signature;
    this.dispatchEvent(new CustomEvent('tabregister', {
      detail: {
        tabsIds,
        setAriaControls: this.setAriaControls.bind(this),
        focusActiveTab: this.focusActiveTab.bind(this)
      }
    }));
  }

  // @ts-expect-error * Automated Type Error Fix
  handleAnalytics(event) {
    event.detail.elementType = 'tab';
  }
  /*LWC compiler v7.1.3*/
}
/** Matches `--wes-g-kx-duration-long` so listing can keep overflow unclipped until the drawer finishes closing. */
TabGroupV2.DROPDOWN_CLOSE_MS = 400;
_registerDecorators(TabGroupV2, {
  publicProps: {
    tabs: {
      config: 0
    },
    flush: {
      config: 0
    },
    idNamespace: {
      config: 0
    },
    dropdownFullBleed: {
      config: 0
    },
    mobile: {
      config: 3
    }
  },
  publicMethods: ["scrollTriggerIntoView"],
  track: {
    stackedTabLayout: 1,
    isDropdownOpen: 1,
    responsiveState: 1
  },
  fields: ["handleDocumentMouseDown", "lastRegisteredTabIdsSignature"]
});
const __lwc_component_class_internal = _registerComponent(TabGroupV2, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;